/**
 * @file Renders a block of code, with an optional copy-to-clipboard action.
 */
import React from "react";
import { Row, Col, theme, Typography } from "antd";

import { W0 } from "@components/Constants";

const MAX_HEIGHT = 320;
const LINE_HEIGHT = 1.6;

type Props = {
  code: string;
  maxHeight?: number | string;
  isCopyable?: boolean;
};

/**
 * Renders the code in a scrollable, styled block, with an optional copy button.
 */
const CodeBlock = ({
  code,
  maxHeight = MAX_HEIGHT,
  isCopyable = false,
}: Props): JSX.Element => {
  const {
    token: {
      borderRadius,
      colorBorderSecondary,
      colorFillQuaternary,
      fontSizeSM,
    },
  } = theme.useToken();

  const preStyle = {
    margin: 0,
    padding: W0,
    border: `1px solid ${colorBorderSecondary}`,
    overflow: "auto" as const,
    maxHeight,
    fontSize: fontSizeSM,
    background: colorFillQuaternary,
    lineHeight: LINE_HEIGHT,
    borderRadius,
  };

  const block = <pre style={preStyle}>{code}</pre>;

  if (!isCopyable) {
    return block;
  }

  return (
    <Row align="middle" gutter={W0} wrap={false}>
      <Col flex="auto" style={{ minWidth: 0 }}>
        {block}
      </Col>

      <Col>
        <Typography.Text copyable={{ text: code }} />
      </Col>
    </Row>
  );
};

export default CodeBlock;
